/**
 * POC animated lobby — first-load reveal.
 *
 * When the lobby first shows: a dark curtain with a radial light sweep wipes open (EffectsLayer
 * `reveal`), and for a fixed window the CSS hooks `html[data-fx-stagger]` / `html[data-fx-reveal]`
 * are stamped so the first cards of the first sections (Game.module.css) and the navigation bar
 * (fx.css) rise in staggered. Once per tab session (sessionStorage, marked at start like the
 * seasonal intro so a hide inside the window does not replay it), skipped on tier `off`, curtain
 * only on `lite`, and it yields entirely to a seasonal intro (halloween / christmas curtain).
 *
 * Stamped in a layout effect so the attributes are on <html> before the first lobby paint: cards
 * mounting inside the window get the stagger, later ones keep their stock fade.
 */

import { useLayoutEffect } from 'react';
import { willSeasonalIntroPlay } from '../hooks/useSeasonalPalette';
import { effectsBus } from './effectsBus';
import { FX_ENABLED } from './fxFlag';
import { getMotionTier } from './motionTier';
import { stampHtmlAttribute } from './oneShot';

const PLAYED_KEY = 'lobby.fxRevealPlayed';
// Longest stagger delay (8 cards x 30 ms behind the 350 ms curtain) plus the 420 ms rise, with room
// for the lazy section chunk to land.
const STAGGER_WINDOW_MS = 1800;

const hasPlayed = (): boolean => {
  try {
    return sessionStorage.getItem(PLAYED_KEY) === '1';
  } catch {
    return false;
  }
};

const markPlayed = (): void => {
  try {
    sessionStorage.setItem(PLAYED_KEY, '1');
  } catch {
    // ignore
  }
};

/** QA panel: forget that the reveal played so the next lobby show replays it. */
export const resetFirstLoadReveal = (): void => {
  try {
    sessionStorage.removeItem(PLAYED_KEY);
  } catch {
    // ignore
  }
};

export const useFirstLoadReveal = (lobbyReady: boolean, seasonalBranding: string | undefined): void => {
  useLayoutEffect(() => {
    if (!FX_ENABLED || !lobbyReady || hasPlayed()) return undefined;
    const tier = getMotionTier();
    if (tier === 'off') return undefined;

    markPlayed();
    // A seasonal curtain is about to play: ours would collide with it (two full-screen layers).
    if (willSeasonalIntroPlay(seasonalBranding)) return undefined;

    const cleanups: Array<() => void> = [];
    if (tier === 'full') {
      cleanups.push(stampHtmlAttribute('data-fx-stagger', STAGGER_WINDOW_MS));
      cleanups.push(stampHtmlAttribute('data-fx-reveal', STAGGER_WINDOW_MS));
    }
    // The layer is a lazy chunk; if it has not landed yet (slow network) the curtain is skipped
    // rather than flashing dark over an already painted lobby.
    const revealId = effectsBus.isLayerMounted() ? effectsBus.play('reveal', {}) : null;

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      if (revealId !== null) effectsBus.cancel(revealId);
    };
  }, [lobbyReady, seasonalBranding]);
};
